// frontend/src/pages/LabNodesPage.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link as MLink,
} from "@mui/material";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import TerminalIcon from "@mui/icons-material/Terminal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WebAssetIcon from "@mui/icons-material/WebAsset";

import api, { getUserMessage } from "../api/axios";
import { pageWrap, tableHeaderCell, tableRow, btnPrimary, minutesFmt } from "../utils/pageStyles";
import { useTranslation } from "react-i18next";

function safeLower(s) {
  return (s ?? "").toString().toLowerCase();
}

function statusChip(node) {
  const st = safeLower(node?.status);
  if (st === "started" || st === "running" || st === "on") return { label: "Running", color: "success" };
  if (st === "stopped" || st === "off") return { label: "Stopped", color: "default" };
  return { label: st || "unknown", color: "default" };
}

export default function LabNodesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reservationId } = useParams();

  const rid = useMemo(() => {
    const n = Number(reservationId);
    return Number.isFinite(n) ? n : null;
  }, [reservationId]);

  const [loading, setLoading] = useState(true);
  const [nodes, setNodes] = useState([]);
  const [linksPayload, setLinksPayload] = useState(null);
  const [accessLinks, setAccessLinks] = useState({});

  const [busy, setBusy] = useState({
    refresh: false,
    startAll: false,
    stopAll: false,
    byNode: {}, // { [node_id]: { start:boolean, stop:boolean } }
  });

  const [msg, setMsg] = useState(null); // {text,type}
  const [error, setError] = useState("");

  const webconsoleBase = (linksPayload?.webconsole_base || "").trim();

  const getConsoleLink = useCallback(
    (nodeId) => {
      const entry = accessLinks?.[String(nodeId)];
      return entry?.link || null; // telnet:// / vnc:// / spice://
    },
    [accessLinks]
  );

  const getConsoleMeta = useCallback(
    (nodeId) => {
      const entry = accessLinks?.[String(nodeId)];
      if (!entry) return null;
      return {
        console_type: entry.console_type || null,
        console_port: entry.console_port || null,
        link: entry.link || null,
      };
    },
    [accessLinks]
  );

  const webConsoleUrl = useCallback(
    (nodeId) => {
      if (!webconsoleBase) return null;
      return `${webconsoleBase}?rid=${encodeURIComponent(String(rid ?? ""))}&nid=${encodeURIComponent(String(nodeId))}`;
    },
    [webconsoleBase, rid]
  );

  const setNodeBusy = (nodeId, patch) => {
    setBusy((s) => ({
      ...s,
      byNode: {
        ...(s.byNode || {}),
        [String(nodeId)]: {
          ...(s.byNode?.[String(nodeId)] || {}),
          ...patch,
        },
      },
    }));
  };

  const load = useCallback(async () => {
    if (!rid) {
      setError(t("errors.invalid_id", { defaultValue: "Invalid reservation id" }));
      setLoading(false);
      return;
    }

    try {
      setError("");
      setLoading(true);

      const nodesRes = await api.get(`/gns3/reservations/${rid}/nodes`);

      const rawNodes = nodesRes?.data?.nodes || [];
      setNodes(Array.isArray(rawNodes) ? rawNodes : []);

      // /nodes retourne déjà console_host, console_port, console_type, webconsole_url
      // Plus besoin de /access-links (endpoint inexistant)
      setLinksPayload(nodesRes?.data || null);
      setAccessLinks({});
    } catch (e) {
      setError(getUserMessage(e, t("errors.unknown", { defaultValue: "Unknown error" })));
    } finally {
      setLoading(false);
    }
  }, [rid, t]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    setBusy((s) => ({ ...s, refresh: true }));
    try {
      await load();
      setMsg({ type: "success", text: t("common.refreshed", { defaultValue: "Refreshed" }) });
    } catch (e) {
      setMsg({ type: "error", text: getUserMessage(e, t("errors.unknown", { defaultValue: "Unknown error" })) });
    } finally {
      setBusy((s) => ({ ...s, refresh: false }));
    }
  };

  const startAll = async () => {
    if (!rid) return;
    setBusy((s) => ({ ...s, startAll: true }));
    try {
      await api.post(`/gns3/reservations/${rid}/nodes/start`);
      setMsg({ type: "success", text: t("nodes.all_started", { defaultValue: "All nodes started" }) });
      await load();
    } catch (e) {
      setMsg({ type: "error", text: getUserMessage(e, t("errors.unknown", { defaultValue: "Unknown error" })) });
    } finally {
      setBusy((s) => ({ ...s, startAll: false }));
    }
  };

  const stopAll = async () => {
    if (!rid) return;
    setBusy((s) => ({ ...s, stopAll: true }));
    try {
      await api.post(`/gns3/reservations/${rid}/nodes/stop`);
      setMsg({ type: "success", text: t("nodes.all_stopped", { defaultValue: "All nodes stopped" }) });
      await load();
    } catch (e) {
      setMsg({ type: "error", text: getUserMessage(e, t("errors.unknown", { defaultValue: "Unknown error" })) });
    } finally {
      setBusy((s) => ({ ...s, stopAll: false }));
    }
  };

  const startNode = async (nodeId) => {
    if (!rid) return;
    setNodeBusy(nodeId, { start: true });
    try {
      await api.post(`/gns3/reservations/${rid}/nodes/${nodeId}/start`);
      setMsg({ type: "success", text: t("nodes.node_started", { defaultValue: "Node started" }) });
      await load();
    } catch (e) {
      setMsg({ type: "error", text: getUserMessage(e, t("errors.unknown", { defaultValue: "Unknown error" })) });
    } finally {
      setNodeBusy(nodeId, { start: false });
    }
  };

  const stopNode = async (nodeId) => {
    if (!rid) return;
    setNodeBusy(nodeId, { stop: true });
    try {
      await api.post(`/gns3/reservations/${rid}/nodes/${nodeId}/stop`);
      setMsg({ type: "success", text: t("nodes.node_stopped", { defaultValue: "Node stopped" }) });
      await load();
    } catch (e) {
      setMsg({ type: "error", text: getUserMessage(e, t("errors.unknown", { defaultValue: "Unknown error" })) });
    } finally {
      setNodeBusy(nodeId, { stop: false });
    }
  };

  if (loading) {
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={900}>
            {t("nodes.title", { defaultValue: "Lab Nodes" })}
          </Typography>
        </Stack>

        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={22} />
            <Typography>{t("common.loading", { defaultValue: "Loading…" })}</Typography>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate(-1)} aria-label="back">
            <ArrowBackIcon />
          </IconButton>
          <Stack>
            <Typography variant="h6" fontWeight={900}>
              {t("nodes.title", { defaultValue: "Lab Nodes" })} #{rid ?? "?"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("nodes.project", { defaultValue: "Project" })}:{" "}
              <b>{linksPayload?.project_id || t("common.unknown", { defaultValue: "Unknown" })}</b>{" "}
              {linksPayload?.host ? (
                <>
                  • {t("nodes.controller_host", { defaultValue: "Controller host" })}: <b>{linksPayload.host}</b>
                </>
              ) : null}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={t("common.refresh", { defaultValue: "Refresh" })}>
            <span>
              <IconButton onClick={refresh} disabled={busy.refresh}>
                {busy.refresh ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={busy.startAll ? <CircularProgress size={14} /> : <PlayArrowIcon />}
            onClick={startAll}
            disabled={busy.startAll}
          >
            {t("nodes.start_all", { defaultValue: "Start all" })}
          </Button>

          <Button
            variant="outlined"
            color="warning"
            startIcon={busy.stopAll ? <CircularProgress size={14} /> : <StopIcon />}
            onClick={stopAll}
            disabled={busy.stopAll}
          >
            {t("nodes.stop_all", { defaultValue: "Stop all" })}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Evolution space: Lab info */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={900}>{t("nodes.lab_info", { defaultValue: "Lab info (optional)" })}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {t("nodes.description", { defaultValue: "Description" })}:{" "}
              <i>{t("nodes.todo_desc", { defaultValue: "(to integrate: lab/reservation description)" })}</i>
            </Typography>

            <Box
              sx={{
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                minHeight: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0.85,
              }}
            >
              <Typography variant="body2">
                {t("nodes.topology_placeholder", { defaultValue: "Topology preview (future): SVG/PNG render" })}
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Nodes table */}
      <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography fontWeight={900}>{t("nodes.list", { defaultValue: "Nodes" })}</Typography>

            <Typography variant="caption" color="text.secondary">
              {t("nodes.console_hint", {
                defaultValue: "Tip: Telnet/VNC links work best over VPN (WireGuard).",
              })}
            </Typography>
          </Stack>

          <Divider />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("nodes.columns.name", { defaultValue: "Name" })}</TableCell>
                <TableCell>{t("nodes.columns.type", { defaultValue: "Type" })}</TableCell>
                <TableCell>{t("nodes.columns.status", { defaultValue: "Status" })}</TableCell>
                <TableCell>{t("nodes.columns.console", { defaultValue: "Console" })}</TableCell>
                <TableCell align="right">{t("nodes.columns.actions", { defaultValue: "Actions" })}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(nodes || []).map((n) => {
                const chip = statusChip(n);
                // console_type et console_port viennent directement de /nodes
                const ct = String(n.console_type || "").toLowerCase();
                const hasConsole = (ct === "telnet" || ct === "vnc") && !!n.console_port;
                const busyNode = busy.byNode?.[String(n.node_id)] || {};
                // Conserver console/link pour compatibilité affichage chip
                const console = { console_type: ct, console_port: n.console_port };
                const link = n.telnet_url || n.vnc_url || null;
                const wc = hasConsole ? `/lab/${rid}/console/${n.node_id}` : null;

                return (
                  <TableRow key={n.node_id} hover>
                    <TableCell>
                      <Stack spacing={0.2}>
                        <Typography fontWeight={800}>{n.name || n.node_id}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {n.node_id}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{n.node_type || "-"}</Typography>
                    </TableCell>

                    <TableCell>
                      <Chip size="small" label={chip.label} color={chip.color} />
                    </TableCell>

                    <TableCell>
                      {console?.console_type ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={`${(console.console_type || "").toUpperCase()}${console.console_port ? ` : ${console.console_port}` : ""}`}
                          />
                          {hasConsole ? (
                            <Tooltip title={t("nodes.open_console", { defaultValue: "Open console link" })}>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/lab/${rid}/console/${n.node_id}`)}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Chip size="small" label={t("common.none", { defaultValue: "None" })} />
                          )}
                        </Stack>
                      ) : (
                        <Chip size="small" label={t("nodes.no_console", { defaultValue: "No console" })} />
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title={t("nodes.start_node", { defaultValue: "Start node" })}>
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => startNode(n.node_id)}
                              disabled={!!busyNode.start}
                              startIcon={busyNode.start ? <CircularProgress size={12} /> : <PlayArrowIcon />}
                            >
                              {t("common.start", { defaultValue: "Start" })}
                            </Button>
                          </span>
                        </Tooltip>

                        <Tooltip title={t("nodes.stop_node", { defaultValue: "Stop node" })}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => stopNode(n.node_id)}
                              disabled={!!busyNode.stop}
                              startIcon={busyNode.stop ? <CircularProgress size={12} /> : <StopIcon />}
                            >
                              {t("common.stop", { defaultValue: "Stop" })}
                            </Button>
                          </span>
                        </Tooltip>

                        <Tooltip title={t("nodes.console", { defaultValue: "Console" })}>
                          <span>
                            <Button
                              size="small"
                              variant="text"
                              disabled={!hasConsole}
                              startIcon={<TerminalIcon />}
                              onClick={() => navigate(`/lab/${rid}/console/${n.node_id}`)}
                            >
                              {t("nodes.console", { defaultValue: "Console" })}
                            </Button>
                          </span>
                        </Tooltip>

                        <Tooltip title={t("nodes.webconsole", { defaultValue: "Web console" })}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!wc}
                              startIcon={<WebAssetIcon />}
                              onClick={() => navigate(`/lab/${rid}/console/${n.node_id}`)}
                            >
                              {t("nodes.webconsole", { defaultValue: "Web" })}
                            </Button>
                          </span>
                        </Tooltip>

                        {/* Optionnel: montrer le lien brut */}
                        {hasConsole && (
                          <Tooltip title={t("nodes.copy_link_hint", { defaultValue: "Console link" })}>
                            <MLink
                              href={link}
                              target="_blank"
                              rel="noreferrer"
                              underline="hover"
                              sx={{ alignSelf: "center", fontSize: 12 }}
                            >
                              {t("nodes.link", { defaultValue: "link" })}
                            </MLink>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}

              {(!nodes || nodes.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary">
                      {t("nodes.empty", { defaultValue: "No nodes found." })}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Stack>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={!!msg}
        autoHideDuration={4500}
        onClose={() => setMsg(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={msg?.type || "info"} onClose={() => setMsg(null)} sx={{ width: "100%" }}>
          {msg?.text}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
